import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import claimService from './claimService';
import { setClaimDocuments } from '../documents/documentSlice';

// Initial state
const initialState = {
  claims: [],
  selectedClaim: null,
  claimHistory: [],
  lastChanged: null,
  lastChangedBy: null,
  pagination: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  isSuccess: false,
  message: '',
  submitSuccess: false,
  // Assignment loading states
  isAssigning: false,
  isReleasing: false,
  assignmentError: null,
  assignmentMessage: '',
};

// Get all claims
export const getAllClaims = createAsyncThunk(
  'claims/getAll',
  async (options = {}, thunkAPI) => {
    try {
      const response = await claimService.getAllClaims(options);
      return response;
    } catch (error) {
      // Don't reject on abort errors
      if (error.name === 'AbortError') {
        return thunkAPI.rejectWithValue('Request cancelled');
      }
      
      // Check if it's an authentication error
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // Handle auth errors silently in production
      }
      const message = error.message || 'Failed to fetch claims';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get claims by user ID
export const getClaimsByUserId = createAsyncThunk(
  'claims/getByUserId',
  async ({ userId, ...params }, thunkAPI) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      // Extract signal from params if present
      const { signal, ...otherParams } = params;
      const options = signal ? { signal } : {};
      
      const response = await claimService.getClaimsByUserId(userId, otherParams, options);
      return response;
    } catch (error) {
      // Don't reject on abort errors
      if (error.name === 'AbortError') {
        return thunkAPI.rejectWithValue('Request cancelled');
      }
      
      // Check if it's an authentication error
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // Handle auth errors silently in production
      }
      if (error.message.includes('403') || error.message.includes('Forbidden')) {
        // Handle forbidden errors silently in production
      }
      const message = error.message || 'Failed to fetch user claims';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get claim by ID
export const getClaimById = createAsyncThunk(
  'claims/getById',
  async (claimId, thunkAPI) => {
    try {
      const claimData = await claimService.getClaimById(claimId);
      
      // If the claim has documents, update the documents slice
      if (claimData && Array.isArray(claimData.documents)) {
        thunkAPI.dispatch(setClaimDocuments(claimData.documents));
      }
      
      return claimData;
    } catch (error) {
      const message = error.message || 'Failed to fetch claim';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Create new claim
export const createClaim = createAsyncThunk(
  'claims/create',
  async (claimData, thunkAPI) => {
    try {
      // Get current user from state to track who submitted the claim
      const state = thunkAPI.getState();
      const currentUser = state.auth.user;
      
      // Add submittedById field to track claim ownership
      const claimDataWithSubmitter = {
        ...claimData,
        submittedById: currentUser?.userId,
      };
      
        // Console log removed for production
      const response = await claimService.createClaim(claimDataWithSubmitter);
        // Console log removed for production
      return response;
    } catch (error) {
        // Console log removed for production
      const message = error.message || 'Failed to create claim';
      
      // Log more details for debugging
      if (error.message.includes('Validation failed')) {
        // Console log removed for production
      }
      
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update claim
export const updateClaim = createAsyncThunk(
  'claims/update',
  async ({ claimId, claimData }, thunkAPI) => {
    try {
      return await claimService.updateClaim(claimId, claimData);
    } catch (error) {
      const message = error.message || 'Failed to update claim';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete claim
export const deleteClaim = createAsyncThunk(
  'claims/delete',
  async (claimId, thunkAPI) => {
    try {
      await claimService.deleteClaim(claimId);
      return claimId;
    } catch (error) {
      const message = error.message || 'Failed to delete claim';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get claims by status
export const getClaimsByStatus = createAsyncThunk(
  'claims/getByStatus',
  async ({ status, ...options }, thunkAPI) => {
    try {
      const response = await claimService.getClaimsByStatus(status, options);
      return response;
    } catch (error) {
      console.error('❌ [ClaimSlice] getClaimsByStatus error:', error);
      const message = error.message || 'Failed to fetch claims by status';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update claim status
export const updateClaimStatus = createAsyncThunk(
  'claims/updateStatus',
  async ({ claimId, status, comment }, thunkAPI) => {
    try {
      const result = await claimService.updateClaimStatus(claimId, status, comment);
      
      // Automatically fetch the updated claim data
      thunkAPI.dispatch(getClaimById(claimId));
      
      return result;
    } catch (error) {
      const message = error.message || 'Failed to update claim status';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Review claim and mark it as ready for approval
export const reviewClaim = createAsyncThunk(
  'claims/review',
  async ({ claimId, comments }, thunkAPI) => {
    try {
      const result = await claimService.reviewClaim(claimId, comments);
      
      // Automatically fetch the updated claim data
      thunkAPI.dispatch(getClaimById(claimId));
      
      return result;
    } catch (error) {
      const message = error.message || 'Failed to review claim';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Recommend claim for approval (Claims Officer action)
export const recommendClaimForApproval = createAsyncThunk(
  'claims/recommend',
  async ({ claimId, recommendationNotes, additionalInfo }, thunkAPI) => {
    try {
      const result = await claimService.recommendClaimForApproval(claimId, {
        notes: recommendationNotes, // Backend expects 'notes' field
        additionalInfo
      });
      
      // Automatically fetch the updated claim data
      thunkAPI.dispatch(getClaimById(claimId));
      
      return result;
    } catch (error) {
      const message = error.message || 'Failed to recommend claim for approval';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Approve or reject claim (Approver action)
export const approveClaim = createAsyncThunk(
  'claims/approve',
  async ({ claimId, decision, approvalNotes }, thunkAPI) => {
    try {
      const result = await claimService.approveClaim(claimId, {
        decision,
        approvalNotes
      });
      
      // Automatically fetch the updated claim data
      thunkAPI.dispatch(getClaimById(claimId));
      
      return result;
    } catch (error) {
      const message = error.message || 'Failed to process claim approval';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Authorize payment for approved claim (Finance Officer action)
export const authorizePayment = createAsyncThunk(
  'claims/authorizePayment',
  async ({ claimId, authorizationNotes, paymentMethod = 'BANK_TRANSFER' }, thunkAPI) => {
    try {
      const result = await claimService.authorizePayment(claimId, {
        authorizationNotes,
        paymentMethod
      });
      
      // Automatically fetch the updated claim data
      thunkAPI.dispatch(getClaimById(claimId));
      
      return result;
    } catch (error) {
      const message = error.message || 'Failed to authorize payment';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Assign claim to current user (Claims Officer action)
export const assignClaimToSelf = createAsyncThunk(
  'claims/assignToSelf',
  async ({ claimId, comment }, thunkAPI) => {
    try {
      // Permission validation at Redux level
      const state = thunkAPI.getState();
      const user = state.auth.user;
      const userRoles = user?.userRoles?.map(ur => ur.role?.name) || [];
      
      if (!userRoles.includes('Claims_Officer')) {
        return thunkAPI.rejectWithValue('Only Claims Officers can assign claims to themselves');
      }
      
      const result = await claimService.assignClaimToSelf(claimId, comment);
      
      // Return the claim ID and assignment data for state updates
      return {
        claimId: parseInt(claimId),
        assignmentData: result.data,
        message: result.message
      };
    } catch (error) {
      const message = error.message || 'Failed to assign claim';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Release claim from current user (Claims Officer action)
export const releaseClaimFromSelf = createAsyncThunk(
  'claims/releaseFromSelf',
  async ({ claimId, comment }, thunkAPI) => {
    try {
      // Permission validation at Redux level
      const state = thunkAPI.getState();
      const user = state.auth.user;
      const userRoles = user?.userRoles?.map(ur => ur.role?.name) || [];
      
      if (!userRoles.includes('Claims_Officer')) {
        return thunkAPI.rejectWithValue('Only Claims Officers can release claims from themselves');
      }
      
      const result = await claimService.releaseClaimFromSelf(claimId, comment);
      
      // Return the claim ID and release data for state updates
      return {
        claimId: parseInt(claimId),
        assignmentData: result.data,
        message: result.message
      };
    } catch (error) {
      const message = error.message || 'Failed to release claim';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Claim slice
export const claimSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    resetClaimState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.error = null;
      state.message = '';
      state.submitSuccess = false;
    },
    clearSelectedClaim: (state) => {
      state.selectedClaim = null;
    },
    clearClaimMessage: (state) => {
      state.message = '';
      state.error = null;
      state.isSuccess = false;
      state.submitSuccess = false;
    },
    clearAssignmentMessage: (state) => {
      state.assignmentMessage = '';
      state.assignmentError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all claims
      .addCase(getAllClaims.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(getAllClaims.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        state.lastFetchedAt = Date.now();
        // Handle response structure with claims and pagination
        state.claims = action.payload.claims || [];
        state.pagination = action.payload.pagination || null;
        state.message = '';
      })
      .addCase(getAllClaims.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        
        // Handle 304 Not Modified differently - don't clear existing data
        if (action.payload && action.payload.includes && action.payload.includes('use cached data')) {
          // Keep existing claims and pagination, just update state
          state.error = null;
          state.message = '';
        } else {
          // Handle other errors normally
          state.error = action.payload;
          state.message = action.payload;
        }
      })
      
      // Get claims by user ID
      .addCase(getClaimsByUserId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(getClaimsByUserId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        state.lastFetchedAt = Date.now();
        // Handle response structure with claims and pagination
        state.claims = action.payload.claims || [];
        state.pagination = action.payload.pagination || null;
        state.message = '';
      })
      .addCase(getClaimsByUserId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isSuccess = false;
        state.claims = []; // Clear claims on error
        state.pagination = null; // Clear pagination on error
        state.message = action.payload;
      })
      
      // Get claim by ID
      .addCase(getClaimById.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getClaimById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.claim = action.payload;
        state.selectedClaim = action.payload;
        
        // Map audit logs to claim history
        const auditLogs = action.payload.auditLogs || [];
        
        state.claimHistory = auditLogs.map(log => {
          // Parse details to separate readable text from JSON metadata
          let cleanDetails = log.details;
          let metadata = null;
          
          if (log.details && log.details.includes(' | Metadata: ')) {
            const parts = log.details.split(' | Metadata: ');
            cleanDetails = parts[0];
            try {
              metadata = JSON.parse(parts[1]);
            } catch (e) {
              // If parsing fails, keep original details
              cleanDetails = log.details;
            }
          }
          
          const processedLog = {
            id: log.id,
            action: log.action,
            details: cleanDetails,
            metadata: metadata,
            changedAt: log.createdAt,
            changedBy: log.user ? {
              id: log.user.id,
              username: log.user.username
            } : null
          };
          
          return processedLog;
        });
        
        state.lastChanged = action.payload.lastChanged;
        state.lastChangedBy = action.payload.lastChangedBy;
        state.isError = false;
      })
      .addCase(getClaimById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.error.message;
      })
      
      // Create claim
      .addCase(createClaim.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.submitSuccess = false;
        state.message = '';
      })
      .addCase(createClaim.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.submitSuccess = true;
        state.error = null;
        state.claims.push(action.payload);
        state.message = 'Claim submitted successfully';
      })
      .addCase(createClaim.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isSuccess = false;
        state.submitSuccess = false;
        state.message = action.payload;
      })
      
      // Update claim
      .addCase(updateClaim.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(updateClaim.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        state.claims = state.claims.map(claim => 
          claim.id === action.payload.id ? action.payload : claim
        );
        if (state.selectedClaim?.id === action.payload.id) {
          state.selectedClaim = action.payload;
        }
        state.message = 'Claim updated successfully';
      })
      .addCase(updateClaim.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isSuccess = false;
        state.message = action.payload;
      })
      
      // Delete claim
      .addCase(deleteClaim.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(deleteClaim.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        state.claims = state.claims.filter(claim => claim.id !== action.payload);
        if (state.selectedClaim?.id === action.payload) {
          state.selectedClaim = null;
        }
        state.message = 'Claim deleted successfully';
      })
      .addCase(deleteClaim.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isSuccess = false;
        state.message = action.payload;
      })
      
      // Get claims by status
      .addCase(getClaimsByStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(getClaimsByStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        state.lastFetchedAt = Date.now();
        // Handle response structure with claims and pagination
        state.claims = action.payload.claims || [];
        state.pagination = action.payload.pagination || null;
        state.message = '';
      })
      .addCase(getClaimsByStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        
        // Handle 304 Not Modified differently - don't clear existing data
        if (action.payload && action.payload.includes && action.payload.includes('use cached data')) {
          // Keep existing claims and pagination, just update state
          state.error = null;
          state.message = '';
        } else {
          // Handle other errors normally
          state.error = action.payload;
          state.message = action.payload;
        }
      })
      
      // Update claim status
      .addCase(updateClaimStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(updateClaimStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        state.claims = state.claims.map(claim => 
          claim.id === action.payload.id ? action.payload : claim
        );
        if (state.selectedClaim?.id === action.payload.id) {
          state.selectedClaim = action.payload;
        }
        state.message = 'Claim status updated successfully';
      })
      .addCase(updateClaimStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.message = action.payload || 'Failed to update claim status';
      })
      
      // Review claim
      .addCase(reviewClaim.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(reviewClaim.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        
        // Update the claim in the list if it exists
        if (action.payload.claim) {
          state.claims = state.claims.map(claim => 
            claim.id === action.payload.claim.id ? action.payload.claim : claim
          );
          
          // Update the selected claim if it matches
          if (state.selectedClaim?.id === action.payload.claim.id) {
            state.selectedClaim = action.payload.claim;
          }
        }
        
        state.message = 'Claim reviewed and marked ready for approval';
      })
      .addCase(reviewClaim.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.message = action.payload || 'Failed to review claim';
      })
      
      // Recommend claim for approval
      .addCase(recommendClaimForApproval.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(recommendClaimForApproval.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        
        // Update the claim in the list if it exists
        if (action.payload.claim) {
          state.claims = state.claims.map(claim => 
            claim.id === action.payload.claim.id ? action.payload.claim : claim
          );
          
          // Update the selected claim if it matches
          if (state.selectedClaim?.id === action.payload.claim.id) {
            state.selectedClaim = action.payload.claim;
          }
        }
        
        state.message = 'Claim assessment submitted successfully';
      })
      .addCase(recommendClaimForApproval.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.message = action.payload || 'Failed to recommend claim for approval';
      })
      
      // Approve claim
      .addCase(approveClaim.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(approveClaim.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        
        // Update the claim in the list if it exists
        if (action.payload.claim) {
          state.claims = state.claims.map(claim => 
            claim.id === action.payload.claim.id ? action.payload.claim : claim
          );
          
          // Update the selected claim if it matches
          if (state.selectedClaim?.id === action.payload.claim.id) {
            state.selectedClaim = action.payload.claim;
          }
        }
        
        const decision = action.payload.data?.status === 'APPROVED' ? 'approved' : 'rejected';
        state.message = `Claim successfully ${decision}`;
      })
      .addCase(approveClaim.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.message = action.payload || 'Failed to process claim approval';
      })
      
      // Authorize payment
      .addCase(authorizePayment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = '';
      })
      .addCase(authorizePayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.error = null;
        
        // Update the claim in the list if it exists
        if (action.payload.claim) {
          state.claims = state.claims.map(claim => 
            claim.id === action.payload.claim.id ? action.payload.claim : claim
          );
          
          // Update the selected claim if it matches
          if (state.selectedClaim?.id === action.payload.claim.id) {
            state.selectedClaim = action.payload.claim;
          }
        }
        
        state.message = action.payload.message || 'Payment authorized successfully';
      })
      .addCase(authorizePayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.message = action.payload || 'Failed to authorize payment';
      })
      
      // Assign claim to self
      .addCase(assignClaimToSelf.pending, (state) => {
        state.isAssigning = true;
        state.assignmentError = null;
        state.assignmentMessage = '';
      })
      .addCase(assignClaimToSelf.fulfilled, (state, action) => {
        state.isAssigning = false;
        state.assignmentError = null;
        state.assignmentMessage = action.payload.message || 'Claim assigned successfully';
        
        // Update the claim in the claims array with assignment data
        const { claimId, assignmentData } = action.payload;
        state.claims = state.claims.map(claim => 
          claim.id === claimId 
            ? { 
                ...claim, 
                assignedToId: assignmentData.assignedToId,
                assignedAt: assignmentData.assignedAt
              }
            : claim
        );
        
        // Update selected claim if it matches
        if (state.selectedClaim?.id === claimId) {
          state.selectedClaim = {
            ...state.selectedClaim,
            assignedToId: assignmentData.assignedToId,
            assignedAt: assignmentData.assignedAt
          };
        }
      })
      .addCase(assignClaimToSelf.rejected, (state, action) => {
        state.isAssigning = false;
        state.assignmentError = action.payload || 'Failed to assign claim';
        state.assignmentMessage = '';
      })
      
      // Release claim from self
      .addCase(releaseClaimFromSelf.pending, (state) => {
        state.isReleasing = true;
        state.assignmentError = null;
        state.assignmentMessage = '';
      })
      .addCase(releaseClaimFromSelf.fulfilled, (state, action) => {
        state.isReleasing = false;
        state.assignmentError = null;
        state.assignmentMessage = action.payload.message || 'Claim released successfully';
        
        // Update the claim in the claims array by removing assignment data
        const { claimId } = action.payload;
        state.claims = state.claims.map(claim => 
          claim.id === claimId 
            ? { 
                ...claim, 
                assignedToId: null,
                assignedAt: null
              }
            : claim
        );
        
        // Update selected claim if it matches
        if (state.selectedClaim?.id === claimId) {
          state.selectedClaim = {
            ...state.selectedClaim,
            assignedToId: null,
            assignedAt: null
          };
        }
      })
      .addCase(releaseClaimFromSelf.rejected, (state, action) => {
        state.isReleasing = false;
        state.assignmentError = action.payload || 'Failed to release claim';
        state.assignmentMessage = '';
      });
  },
});

export const { resetClaimState, clearSelectedClaim, clearClaimMessage, clearAssignmentMessage } = claimSlice.actions;
export default claimSlice.reducer;

// Selectors
export const selectClaims = (state) => state.claims.claims;
export const selectSelectedClaim = (state) => state.claims.claim;
export const selectClaimHistory = (state) => state.claims.claimHistory;
export const selectLastChanged = (state) => state.claims.lastChanged;
export const selectLastChangedBy = (state) => state.claims.lastChangedBy;
export const selectClaimsLoading = (state) => state.claims.isLoading;
export const selectClaimsError = (state) => state.claims.isError;
export const selectClaimsErrorMessage = (state) => state.claims.errorMessage;
export const selectClaimsSuccess = (state) => state.claims.isSuccess;
export const selectClaimsMessage = (state) => state.claims.message;
export const selectSubmitSuccess = (state) => state.claims.submitSuccess;
export const selectPagination = (state) => state.claims.pagination;
export const selectLastFetchedAt = (state) => state.claims.lastFetchedAt;

// Assignment selectors
export const selectIsAssigning = (state) => state.claims.isAssigning;
export const selectIsReleasing = (state) => state.claims.isReleasing;
export const selectAssignmentError = (state) => state.claims.assignmentError;
export const selectAssignmentMessage = (state) => state.claims.assignmentMessage;
